import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  LinearProgress,
  TablePagination,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useTheme } from "@mui/material/styles";
import {
  GetCommunityAdsCall,
  AddCommunityAdCall,
  DeleteCommunityAdCall,
} from "../../redux/api/communityAds.api";

const CTA_OPTIONS = [
  { value: "none", label: "None" },
  { value: "like", label: "Like" },
  { value: "view", label: "View" },
  { value: "follow", label: "Follow" },
  { value: "other", label: "Other / custom" },
];

const CommunityAdsPage = () => {
  const theme = useTheme();
  const [ads, setAds] = useState([]);
  const [page, setPage] = useState(0); // zero-based for MUI
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ctaType, setCtaType] = useState("none");
  const [ctaValue, setCtaValue] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const cardBg =
    theme.palette.background?.card ??
    theme.palette.background?.paper ??
    theme.palette.background?.default;
  const textPrimary = theme.palette.text.primary;

  const loadAds = async (pageIndex = page, pageSize = rowsPerPage) => {
    setLoading(true);
    const apiPage = pageIndex + 1; // API is 1-based
    const res = await GetCommunityAdsCall(apiPage, pageSize);
    setAds(Array.isArray(res.ads) ? res.ads : []);
    setTotal(Number(res.total) || 0);
    setLoading(false);
  };

  useEffect(() => {
    loadAds(0, rowsPerPage);
  }, [rowsPerPage]);

  const handleSubmit = async () => {
    if (!videoFile) {
      setMessage("Video file is required.");
      return;
    }
    setMessage("");
    setUploadProgress(0);
    const res = await AddCommunityAdCall(
      {
        title: title?.trim() || "",
        description: description?.trim() || "",
        cta_type: ctaType,
        cta_value: ctaValue?.trim() || "",
        videoFile,
        thumbnailFile,
      },
      (p) => setUploadProgress(p * 100)
    );
    if (res?.status === 201) {
      setMessage("Sponsored video uploaded successfully.");
      setTitle("");
      setDescription("");
      setCtaType("none");
      setCtaValue("");
      setVideoFile(null);
      setThumbnailFile(null);
      setPage(0);
      await loadAds(0, rowsPerPage);
      setUploadProgress(0);
    } else {
      setMessage(res?.message || "Failed to upload sponsored video.");
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm("Delete this sponsored video?")) return;
    const res = await DeleteCommunityAdCall(adId);
    if (res?.status === 200) {
      setMessage("Sponsored video deleted.");
      // Reload current page to ensure counts and pages stay in sync
      const nextPage = page;
      await loadAds(nextPage, rowsPerPage);
    } else {
      setMessage(res?.message || "Failed to delete sponsored video.");
    }
  };

  const formatDateTime = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return date.toLocaleString();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={3} pt={3} pb={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h5" sx={{ color: textPrimary }}>
            Community sponsored videos
          </Typography>
          {message && (
            <Typography
              variant="body2"
              color={
                message.toLowerCase().includes("success") ||
                message.toLowerCase().includes("uploaded") ||
                message.toLowerCase().includes("deleted")
                  ? "success.main"
                  : "text.secondary"
              }
            >
              {message}
            </Typography>
          )}
        </MDBox>

        <MDBox mb={4} p={3} borderRadius={2} sx={{ backgroundColor: cardBg }}>
          <Typography variant="h6" sx={{ color: textPrimary, mb: 2 }}>
            Upload new sponsored video
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="CTA type"
                value={ctaType}
                onChange={(e) => setCtaType(e.target.value)}
                select
              >
                {CTA_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="CTA value (optional link / user id / text)"
                value={ctaValue}
                onChange={(e) => setCtaValue(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2" sx={{ color: textPrimary }}>
                  Video file (required)
                </Typography>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setVideoFile(file);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2" sx={{ color: textPrimary }}>
                  Thumbnail (optional)
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setThumbnailFile(file);
                  }}
                />
              </Box>
            </Grid>
            {uploadProgress > 0 && uploadProgress < 100 && (
              <Grid item xs={12}>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Grid>
            )}
            <Grid item xs={12}>
              <MDButton
                color="info"
                variant="contained"
                onClick={handleSubmit}
                disabled={!videoFile}
              >
                Upload sponsored video
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox>
          <Typography variant="h6" sx={{ color: textPrimary, mb: 2 }}>
            Existing sponsored videos
          </Typography>
          <TableContainer component={Paper}>
            {loading && <LinearProgress />}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>CTA</TableCell>
                  <TableCell>Video</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ads?.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No sponsored videos yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {ads?.map((ad) => (
                  <TableRow key={ad.id}>
                    <TableCell>{ad.title || "-"}</TableCell>
                    <TableCell>{ad.description || "-"}</TableCell>
                    <TableCell>
                      {ad.cta_type || "none"}
                      {ad.cta_value ? ` — ${ad.cta_value}` : ""}
                    </TableCell>
                    <TableCell>
                      {ad.thumbnail_url ? (
                        <img
                          src={ad.thumbnail_url}
                          alt="thumbnail"
                          style={{ width: 120, height: "auto", borderRadius: 4 }}
                        />
                      ) : ad.video_url ? (
                        <a href={ad.video_url} target="_blank" rel="noreferrer">
                          Open video
                        </a>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{formatDateTime(ad.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(ad.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                loadAds(newPage, rowsPerPage);
              }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                const newSize = parseInt(event.target.value, 10) || 10;
                setRowsPerPage(newSize);
                setPage(0);
                loadAds(0, newSize);
              }}
              rowsPerPageOptions={[5, 10, 20]}
            />
          </TableContainer>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default CommunityAdsPage;

